import React, { MouseEventHandler } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  trackContentCardLike,
  trackContentCardUnlike,
} from '../../../models/content/analytics';
import { useDispatch } from 'react-redux';
import { contentOperations, contentSelectors } from '../../../models/content';
import { usePatronSelector } from '../../../common/use-patron-selector';
import { useSWRConfig } from 'swr';
import { getContentLikesDataKey } from '../../../common/use-content-likes';
import {
  Button,
  Icon,
  TooltipContent,
  TooltipRoot,
  TooltipTrigger,
} from '@socialchorus/shared-ui-components';

interface IContentActionLike {
  liked: boolean;
  likeCount: number;
  onClick: MouseEventHandler;
  contentTitle: string;
  hideEmptyCount?: boolean;
  showText?: boolean;
}

const ContentActionLike: React.FC<IContentActionLike> = ({
  liked,
  likeCount,
  onClick,
  contentTitle,
  hideEmptyCount = false,
  showText = false,
}) => {
  const className = cx('content__action');

  const { t } = useTranslation(undefined, { useSuspense: false });

  const likeActionKey = liked
    ? 'content.like_remove_action'
    : 'content.like_action';
  const actionLabel = t(likeActionKey, { title: contentTitle });

  return (
    <div className={className}>
      {showText ? (
        <Button
          variant="text"
          label={liked ? t('content.liked') : t('content.like', { count: 1 })}
          onClick={onClick}
          aria-label={actionLabel}
          selected={liked}
          leftIcon={<Icon>thumb_up</Icon>}
        />
      ) : (
        <TooltipRoot>
          <TooltipTrigger asChild>
            <Button
              variant="text"
              onClick={onClick}
              aria-label={actionLabel}
              selected={liked}
              leftIcon={<Icon>thumb_up</Icon>}
              label={`${likeCount || !hideEmptyCount ? likeCount : ''}`}
            />
          </TooltipTrigger>
          <TooltipContent
            description={
              liked ? t('content.like_remove') : t('content.like', { count: 1 })
            }
          />
        </TooltipRoot>
      )}
    </div>
  );
};

type ContentActionLikeWrapperProps = {
  contentId: number;
  analyticsData: IAnalyticsData;
  showText?: boolean;
};

const ContentActionLikeWrapper = ({
  contentId,
  analyticsData,
  showText,
}: ContentActionLikeWrapperProps) => {
  const isLiked = usePatronSelector((s) =>
    contentSelectors.getContentLiked(s, { contentId })
  );
  const likeCount = usePatronSelector((s) =>
    contentSelectors.getContentLikeCount(s, { contentId })
  );
  const dispatch = useDispatch();
  const { mutate } = useSWRConfig();

  const likeContent = (id: number) =>
    dispatch(contentOperations.likeContent(id));
  const unlikeContent = (id: number) =>
    dispatch(contentOperations.unlikeContent(id));

  const handleClick = async () => {
    if (isLiked) {
      trackContentCardUnlike(contentId, analyticsData);
      await unlikeContent(contentId);
    } else {
      trackContentCardLike(contentId, analyticsData);
      await likeContent(contentId);
    }
    // Trigger revalidation on the content likes list
    void mutate(getContentLikesDataKey(contentId));
  };

  const contentTitle = usePatronSelector((s) =>
    contentSelectors.getContentFunctionalTitle(s, { contentId })
  );
  return (
    <ContentActionLike
      liked={isLiked}
      likeCount={likeCount}
      onClick={handleClick}
      contentTitle={contentTitle}
      showText={showText}
    />
  );
};

export { ContentActionLike };

export default ContentActionLikeWrapper;
